import { saleApi } from '../../api';
import { SaleFilter, SaleFinalizeData, SaleRegistrationDTO } from '../../types/sale';


const SaleService = {
  /**
   * Genera una nueva venta
   * POST /api/sales
   */
  generate: async (sale: SaleRegistrationDTO) => {
    const response = await saleApi.addCustom('', sale);
    return response.data;
  },


  /**
  * Actualiza el encabezado de una venta
  * PUT /api/sales/{id}/update-header
  */
  updateHeader: async (id: number, sale: SaleRegistrationDTO) => {
    const response = await saleApi.modifyCustom(`/${id}/update-header`, sale);
    return response.data;
  },


  /**
 * Envía una venta al área de caja
 * PUT /api/sales/{id}/send-to-cashier
 * `comments` viaja aparte porque este flujo no manda el formulario
 * completo (condición/método de pago se capturan después, en Caja).
 */
  sendToCashier: async (id: number, comments?: string) => {
    const response = await saleApi.modifyCustom(`/${id}/send-to-cashier`, { comments });
    return response.data;
  },

  /**
   * Finaliza una venta ya registrada
   * PUT /api/sales/{id}/finalize
   */
  finalize: async (id: number, data: SaleFinalizeData) => {
    const response = await saleApi.modifyCustom(`/${id}/finalize`, data);
    return response.data;
  },

  /**
   * Consulta una venta por su ID
   * GET /api/sales/{id}
   */
  getById: async (id: number) => {
    const response = await saleApi.fetchById(id);
    return response.data;
  },

  /**
   * Lista las ventas filtradas con paginación
   * POST /api/sales/filtered?page=0&size=10&sort=saleDate,desc
   */
  getFiltered: async (filters: SaleFilter, page = 0, size = 10) => {
    const response = await saleApi.addCustom(`/filtered?page=${page}&size=${size}`, filters);
    return response.data;
  },

  getDteError: async (saleId: number) => {
    const response = await saleApi.fetchCustom(`/${saleId}/dte-response`);
    return response.data;
  },

  downloadDteResponse: async (saleId: number): Promise<void> => {
    await saleApi.downloadBlobGet(
      `/${saleId}/dte-response/download`,
      `respuesta-hacienda-venta-${saleId}.json`
    );
  },

  updateBySupport: async (saleId: number, data: object) => {
    const response = await saleApi.patchCustom(`${saleId}/support`, data);
    return response.data;
  },

  /**
   * Anula una venta por ID
   * PUT /api/sales/{id}/cancel?reason=...
   */
  cancel: async (id: number, reason = '') => {
    const url = reason ? `/${id}/cancel?reason=${encodeURIComponent(reason)}` : `/${id}/cancel`;
    const response = await saleApi.modifyCustom(url, {});
    return response.data;
  },

  /**
   * Obtiene las ventas finalizadas en una sucursal (excluyendo notas)
   * GET /api/sales/finalized/{branchId}
   */
  getFinalizedByBranch: async (branchId: number) => {
    const response = await saleApi.fetchCustom(`/finalized/${branchId}`);
    return response.data;
  },

  /**
   * Cuenta las ventas finalizadas en una sucursal (excluyendo notas)
   * GET /api/sales/count/finalized/{branchId}
   */
  countFinalizedByBranch: async (branchId: number) => {
    const response = await saleApi.fetchCustom(`/count/finalized/${branchId}`);
    return response.data;
  },

  /**
 * Cuenta la cantidad total de ventas pendientes
 * GET /api/sales/pending-count
 */
  getPendingCount: async () => {
    const response = await saleApi.fetchCustom('/pending-count');
    return response.data;
  },

  /**
   * Obtiene el resumen general de todas las ventas pendientes (sin filtrar por sucursal).
   * GET /api/sales/pending-summary/all
   */
  getAllPendingSummary: async () => {
    const response = await saleApi.fetchCustom('/pending-summary/all');
    return response.data;
  },


  /**
   * Retorna el resumen de ventas pendientes (filtradas por sucursal si se pasa)
   * GET /api/sales/pending-summary?branchId=...
   */
  getPendingSummary: async (branchId?: number) => {
    const url = branchId ? `/pending-summary?branchId=${branchId}` : '/pending-summary';
    const response = await saleApi.fetchCustom(url);
    return response.data;
  },

  /**
   * Obtiene una venta para continuar su registro
   * GET /api/sales/continue/{id}
   */
  getToContinue: async (id: number) => {
    const response = await saleApi.fetchCustom(`/continue/${id}`);
    return response.data;
  },

  /**
 * Obtiene una venta para edición sin restricción de estado
 * GET /api/sales/edit/{id}
 */
  getForEdit: async (id: number) => {
    const response = await saleApi.fetchCustom(`/edit/${id}`);
    return response.data;
  },

  /**
   * Guarda una venta sin finalizarla
   * PUT /api/sales/{id}/save
   */
  save: async (id: number, data: SaleFinalizeData) => {
    const response = await saleApi.modifyCustom(`/${id}/save`, data);
    return response.data;
  },


  applyGlobalDiscount: async (
    id: number,
    percentage: number,
    confirmReplaceManualDiscounts: boolean = false,
    target: 'PRODUCTS' | 'TOTAL' = 'PRODUCTS'
  ) => {

    const response = await saleApi.modifyCustom(
      `/${id}/global-discount`,
      { percentage, confirmReplaceManualDiscounts, target }
    );

    return response.data;
  },

  /**
   * Elimina el descuento global de la venta.
   * DELETE /api/sales/{id}/global-discount
   */
  removeGlobalDiscount: async (id: number) => {

    const response = await saleApi.removeCustom(
      `/${id}/global-discount`
    );

    return response.data;
  },

  /**
   * Activa el cobro de propina en la venta.
   * PUT /api/sales/{id}/tip
   */
  applyTip: async (id: number) => {

    const response = await saleApi.modifyCustom(
      `/${id}/tip`,
      {}
    );

    return response.data;
  },

  /**
   * Quita el cobro de propina de la venta.
   * DELETE /api/sales/{id}/tip
   */
  removeTip: async (id: number) => {

    const response = await saleApi.removeCustom(
      `/${id}/tip`
    );

    return response.data;
  },

  /**
   * Obtiene la configuración de propina de una sucursal.
   * GET /api/sales/settings/tip/{branchId}
   */
  getTipSettings: async (branchId: number) => {

    const response = await saleApi.fetchCustom(
      `/settings/tip/${branchId}`
    );

    return response.data;
  },

  /**
   * Actualiza la configuración de propina de una sucursal.
   * PUT /api/sales/settings/tip/{branchId}
   */
  updateTipSettings: async (
    branchId: number,
    data: { tipEnabled: boolean; tipPercentage: number }
  ) => {

    const response = await saleApi.modifyCustom(
      `/settings/tip/${branchId}`,
      data
    );

    return response.data;
  },

  /**
 * Obtiene ventas pendientes
 * de cobro para caja.
 *
 * GET /api/sales/pending-cashier/{branchId}
 */
  getPendingCashierSales: async (
    branchId: number,
    sellerId?: number
  ) => {

    // =====================================================
    // 🔹 Query params
    // =====================================================

    const params = new URLSearchParams();

    if (sellerId) {
      params.append('sellerId', sellerId.toString());
    }

    // =====================================================
    // 🔹 URL
    // =====================================================

    const query = params.toString();

    const url = query
      ? `/pending-cashier/${branchId}?${query}`
      : `/pending-cashier/${branchId}`;

    // =====================================================
    // 🔹 Request
    // =====================================================

    const response = await saleApi.fetchCustom(url);

    return response.data;
  },

};

export default SaleService;
